import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { FaLightbulb, FaPencilAlt, FaDatabase, FaChartLine, FaCode, FaMobile } from 'react-icons/fa';

const AboutContainer = styled.div`
  width: 100%;
  min-height: 100vh;
  padding: 4rem 2rem;
  background: #f8f9fa;

  @media (max-width: 768px) {
    padding: 3rem 1rem;
  }
`;

const Content = styled.div`
  max-width: 1200px;
  margin: 0 auto;
`;

const HeaderSection = styled.div`
  margin-bottom: 3rem;
  opacity: 0;
  animation: fadeInUp 0.8s ease forwards;

  @keyframes fadeInUp {
    from {
      opacity: 0;
      transform: translateY(20px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

const AboutUsText = styled(motion.div)`
  text-transform: uppercase;
  letter-spacing: 3px;
  font-size: 0.9rem;
  color: #666;
  margin-bottom: 1rem;
`;

const Title = styled(motion.h2)`
  font-size: 2.5rem;
  color: #333;
  margin-bottom: 1.5rem;
  font-family: 'Playfair Display', serif;
  letter-spacing: 1px;

  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

const Name = styled(motion.span)`
  color: #007bff;
  font-weight: 600;
  position: relative;
  
  &:after {
    content: '';
    position: absolute;
    bottom: -2px;
    left: 0;
    width: 100%;
    height: 2px;
    background: #007bff;
    transform: scaleX(0);
    transform-origin: right;
    transition: transform 0.4s ease;
  }

  &:hover:after {
    transform: scaleX(1);
    transform-origin: left;
  }
`;

const Description = styled(motion.p)`
  font-size: 1.1rem;
  color: #666;
  line-height: 1.8;
  margin-bottom: 2rem;
  max-width: 800px;

  @media (max-width: 768px) {
    font-size: 1rem;
    line-height: 1.6;
  }
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 2rem;
  margin-top: 3rem;

  @media (max-width: 768px) {
    gap: 1.5rem;
    margin-top: 2rem;
  }
`;

const ServiceCard = styled(motion.div)`
  padding: 2rem;
  background: white;
  border-radius: 10px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  position: relative;
  overflow: hidden;
  transition: all 0.3s ease;

  @media (max-width: 768px) {
    padding: 1.5rem;
  }

  &:before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 3px;
    background: ${props => props.color || '#40c4ff'};
    transform: scaleX(0);
    transform-origin: left;
    transition: transform 0.3s ease;
  }

  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 5px 20px rgba(0, 0, 0, 0.1);
    
    &:before {
      transform: scaleX(1);
    }
  }
`;

const ServiceIcon = styled.div`
  width: 50px;
  height: 50px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 1rem;
  color: ${props => props.color || '#666'};
  font-size: 1.8rem;
  transition: transform 0.3s ease;

  ${ServiceCard}:hover & {
    transform: scale(1.1) rotate(5deg);
  }
`;

const ServiceTitle = styled.h3`
  font-size: 1.3rem;
  color: #333;
  margin-bottom: 0.5rem;
  font-family: 'Poppins', sans-serif;
  position: relative;
  
  @media (max-width: 768px) {
    font-size: 1.1rem;
  }
  
  &:after {
    content: '';
    position: absolute;
    bottom: -8px;
    left: 50%;
    width: 0;
    height: 2px;
    background: ${props => props.color || '#40c4ff'};
    transform: translateX(-50%);
    transition: width 0.3s ease;
  }

  ${ServiceCard}:hover &:after {
    width: 50px;
  }
`;

const ServiceDescription = styled.p`
  font-size: 0.9rem;
  color: #666;
  margin-top: 1rem;
  line-height: 1.5;

  @media (max-width: 768px) {
    font-size: 0.85rem;
  }
`;

const About = () => {
  const services = [
    {
      icon: <FaLightbulb />,
      title: "Graphic Design",
      color: "#40c4ff",
      description: "Creating visually appealing designs that communicate your message effectively"
    },
    {
      icon: <FaPencilAlt />,
      title: "Web Design",
      color: "#ff5252",
      description: "Designing beautiful and functional websites that engage your audience"
    },
    {
      icon: <FaDatabase />,
      title: "Software",
      color: "#ffd740",
      description: "Developing custom software solutions to meet your business needs"
    },
    {
      icon: <FaChartLine />,
      title: "Data Analyst",
      color: "#4caf50",
      description: "Analyzing data to provide valuable insights and drive decision making"
    }
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: { 
      opacity: 1,
      transition: { staggerChildren: 0.2 }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6 }
    }
  };

  return (
    <AboutContainer>
      <Content>
        <HeaderSection>
          <AboutUsText
            initial={{ opacity: 0, y: -20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            ABOUT US
          </AboutUsText>
          <Title
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            WHO AM I?
          </Title>
          <Description
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.4 }}
          >
            I am Shlok Sonker, a passionate BTech student specializing in Computer Science, with a keen interest in Frontend Development and Data Analysis. Currently pursuing my degree while actively working on real-world projects to enhance my practical skills.
          </Description>
          <Description
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.6 }}
          >
            My journey combines academic excellence with hands-on experience in web technologies and data analysis. I'm constantly learning and applying new technologies to create innovative solutions, while maintaining a strong focus on user experience and clean code practices.
          </Description>
        </HeaderSection>

        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
        >
          <Grid>
            {services.map((service, index) => (
              <ServiceCard
                key={index}
                variants={itemVariants}
                color={service.color}
              >
                <ServiceIcon color={service.color}>
                  {service.icon}
                </ServiceIcon>
                <ServiceTitle color={service.color}>
                  {service.title}
                </ServiceTitle>
                <ServiceDescription>
                  {service.description}
                </ServiceDescription>
              </ServiceCard>
            ))}
          </Grid>
        </motion.div>
      </Content>
    </AboutContainer>
  );
};

export default About; 